import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { Box, CircularProgress, Dialog, DialogContent, Typography, Card, CardContent, List, ListItem, Link } from "@mui/material";
import { QlikEmbedConfig, type HostConfig } from "@qlik/embed-react"
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "./store/store";
import { ScrollToTop } from "./components/scroll-to-top/ScrollToTop";
import GlobalSnackbar from "./components/snackbar/GlobalSnackbar";
import { getQlikResources } from "./services/getQlikResources";
import { getQlikApp } from "./services/qlikService";
import { QLIK_APP_REF } from "./constants/qlik";
import { getQlikAppSelections } from "./services/qlikSelectionService";
import { initQlikCalcVariables } from "./store/slice/qlikVariableSlice";
import { hideSnackbar, showSnackbar } from "./store/slice/snackbarSlice";
import PdfReport from "./components/views/pdf-report/PdfReport";
import { CYBER_CONFIG } from "./constants";
// import "./App.scss";

const App: React.FC = () => {
  const [dialogIsVisible, setDialogIsVisible] = useState(false);
  const [resourcesReady, setResourcesReady] = useState(false);
  const resourcesReadyRef = useRef(false);
  const [hostConfig, setHostConfig] = useState<HostConfig | undefined>(undefined);
  const location = useLocation();

  const showPdfReport = useSelector((state: RootState) => state.uiProps.showPdfReport);
  // const showPdfReport = true;
  const useAppBackground = useSelector((state: RootState) => state.uiProps.useAppBackground);
  const backgroundImageUrl = useSelector((state: RootState) => state.uiProps.backgroundImageUrl);
  const isAppLoading = useSelector((state: RootState) => state.uiProps.isAppLoading);
  
  const dispatch = useDispatch();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!resourcesReadyRef.current) {
        dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.loadQlikErrorMessage }));
      }
    }, CYBER_CONFIG.qlikErrorMessageTimeout);

    const fetchResources = async () => {
      try {
        const config = await getQlikResources("qlik-servers");
        const appConfig = await getQlikResources("qlik-apps");
        const calcVars = await getQlikResources("qlik-variables");
        dispatch(initQlikCalcVariables({ varObjName: "calcVars", varObject: calcVars.calcVars || {} }));
        dispatch(initQlikCalcVariables({ varObjName: "calcVars2", varObject: calcVars.calcVars2 || {} }));
  
        const appDef = appConfig[QLIK_APP_REF];
        const serverDef: HostConfig | any = config[appDef?.serverRef];
        const { host, port, secure, prefix, authType } = serverDef;
        const qlikServerPrefix = prefix !== "" ? `/${prefix}` : "";
        const qlikServerPath = `${(secure ? "https://" : "http://") + host + (port ? `:${port}` : "") + qlikServerPrefix}/`;
        setHostConfig({host: qlikServerPath, crossSiteCookies: true, authType });
        const qlikApp = await getQlikApp(QLIK_APP_REF);
        const isReady = !!qlikApp;
        resourcesReadyRef.current = isReady;
        setResourcesReady(isReady);
        //hiding snackbar in case it was shown due to timeout and then resources loaded successfully
        if (isReady) {
          dispatch(hideSnackbar());
        }
        await qlikApp.clearAll()
        await getQlikAppSelections(QLIK_APP_REF);
      } catch (error) {
        console.error("Failed to load hostConfig:", error);
        dispatch(showSnackbar({ reason: "error", message: CYBER_CONFIG.loadQlikErrorMessage }));
      }
    };
    fetchResources();

    return () => clearTimeout(timeoutId);
  }, []);

  // Handle logic when resources are ready
  useEffect(() => {
    const fetchGenericObject = async () => {
      if (resourcesReady) {
        // Check browser compatibility
        if (navigator.userAgent.includes("MSIE") || navigator.userAgent.includes("Trident")) {
          setDialogIsVisible(true);
          return;
        }
      }
    }

    fetchGenericObject();
  }, [resourcesReady]);

  // Reset window scroll to top on route changes
  useEffect(() => {
    // Ensure immediate jump to top on navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    
    <QlikEmbedConfig.Provider value={hostConfig}>
      <GlobalSnackbar />
      
      <Box
        id="app-container"
        sx={{
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          // Create pseudo-element for background with opacity
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: useAppBackground ? `url("${backgroundImageUrl}")` : 'none',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            opacity: .15, // Adjust opacity here (0-1)
            transition: 'opacity 0.3s ease-in-out',
            pointerEvents: 'none', // Allow clicks to pass through
          },
        }}
      >
        {!resourcesReady ? (  //Need to be showed when Qlik app is opened
          <Box
            id="curtain"
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                textAlign: 'center',
              }}
            >
              Chubb Cyber Index
            </Typography>
            <CircularProgress size={70} thickness={7} color="primary" />
          </Box>
        ) : (
          <Outlet />
          // <Typography variant="body1" gutterBottom>
          //         Test
          // </Typography>
        )}
        {/* <GlobalSnackbar /> */}

        {/* Scroll to Top Button */}
        {resourcesReady && <ScrollToTop threshold={400} />}

        {/* Route Change Loader Overlay */}
        {isAppLoading && (
          <Box
            id="route-loader"
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 9998, // Below curtain (9999) but above everything else
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(4px)",
            }}
          >
            <CircularProgress size={60} thickness={5} color="primary" />
          </Box>
        )}

        {/* Dialog for Unsupported Browsers */}
        <Dialog open={dialogIsVisible} maxWidth="sm" fullWidth>
          <DialogContent>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  <Link
                    href="https://blogs.windows.com/windowsexperience/2021/05/19/the-future-of-internet-explorer-on-windows-10-is-in-microsoft-edge/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Quality 2.0 does not support this browser
                  </Link>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  You can use one of the suggested modern browsers below:
                </Typography>
                <List>
                  <ListItem>Chrome</ListItem>
                  <ListItem>Edge</ListItem>
                  <ListItem>Firefox</ListItem>
                </List>
              </CardContent>
            </Card>
          </DialogContent>
        </Dialog>
      </Box>
      {/* PDF Report Component */}
      {showPdfReport && <PdfReport />}
    </QlikEmbedConfig.Provider>
  );
};

export default App;